import React, { useState } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { logError } from '../api';
import { 
    isAdmin, 
    isPropietario, 
    isGestion, 
    isEspectador, 
    isJefeComerciales
} from '../services/AuthService';
import LeadCard from './LeadCard';
import LeadStatusChangeModal from './LeadStatusChangeModal';
import LeadDetailModal from './LeadDetailModal';
import './LeadKanbanBoard.css';

const LeadKanbanBoard = ({ leads, loading, hasPendingLeads, onReload }) => {
    const [dragOverColumn, setDragOverColumn] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [targetStatusId, setTargetStatusId] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedLeadId, setSelectedLeadId] = useState(null);

    const navigate = useNavigate();
    const canSelectSalesRep = isAdmin() || isPropietario() || isGestion() || isEspectador() || isJefeComerciales();

    // Estados predefinidos para las columnas
    const statusColumns = [
        { id: 1, name: 'Pendiente de atender', color: '#ffc107', bgColor: '#fff3cd' },
        { id: 2, name: 'Seguimiento', color: '#17a2b8', bgColor: '#d1ecf1' },
        { id: 3, name: 'Venta', color: '#28a745', bgColor: '#d4edda' },
        { id: 4, name: 'No Venta', color: '#dc3545', bgColor: '#f8d7da' }
    ];





    const handleManageLead = (lead) => {
        setSelectedLeadId(lead.id);
        setShowDetailModal(true);
    };

    const handleStatusChange = (leadId, newStatusId) => {
        const lead = leads.find(l => l.id === leadId);
        if (lead && lead.statusId !== newStatusId) {
            setSelectedLead(lead);
            setTargetStatusId(newStatusId);
            setShowStatusModal(true);
        }
    };

    const handleStatusChanged = async () => {
        await onReload();
    };

    // Agrupar leads por estado
    const getLeadsByStatus = (statusId) => {
        return leads.filter(lead => lead.statusId === statusId);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnter = (e, statusId) => {
        e.preventDefault();
        setDragOverColumn(statusId);
    };

    const handleDragLeave = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setDragOverColumn(null);
        }
    };

    const handleDrop = async (e, targetStatusId) => {
        e.preventDefault();
        setDragOverColumn(null);
        try {
            const data = JSON.parse(e.dataTransfer.getData('text/plain'));
            const { leadId, currentStatus } = data;
            
            if (currentStatus !== targetStatusId) {
                await handleStatusChange(leadId, targetStatusId);
            }
        } catch (error) {
            console.error('Error handling drop:', error);
        }
    };

    return (
        <Container fluid className="kanban-container">

            <div className="kanban-board">
                {statusColumns.map(status => {
                    const statusLeads = getLeadsByStatus(status.id);
                    return (
                        <div 
                            key={status.id} 
                            className={`kanban-column ${dragOverColumn === status.id ? 'drag-over' : ''}`}
                            onDragOver={handleDragOver}
                            onDragEnter={(e) => handleDragEnter(e, status.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, status.id)}
                        >
                            <div 
                                className="kanban-column-header"
                                style={{ backgroundColor: status.bgColor, borderColor: status.color }}
                            >
                                <h5 style={{ color: status.color, margin: 0 }}>
                                    {status.name}
                                    <span className="badge bg-secondary ms-2">{statusLeads.length}</span>
                                </h5>
                            </div>
                            <div className="kanban-column-content">
                                {loading ? (
                                    <div className="text-center p-3">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="visually-hidden">Cargando...</span>
                                        </div>
                                    </div>
                                ) : (
                                    statusLeads.map(lead => (
                                        <LeadCard
                                            key={lead.id}
                                            lead={lead}
                                            onManage={handleManageLead}
                                            onStatusChange={handleStatusChange}
                                            statusColumns={statusColumns}
                                            canSelectSalesRep={canSelectSalesRep}
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            
            <LeadStatusChangeModal
                show={showStatusModal}
                handleClose={() => setShowStatusModal(false)}
                lead={selectedLead}
                newStatusId={targetStatusId}
                statusName={statusColumns.find(s => s.id === targetStatusId)?.name || ''}
                onStatusChanged={handleStatusChanged}
            />
            
            <LeadDetailModal
                show={showDetailModal}
                handleClose={() => setShowDetailModal(false)}
                leadId={selectedLeadId}
                onLeadUpdated={onReload}
            />
        </Container>
    );
};

export default LeadKanbanBoard;